import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      services: 'Services',
      about: 'About',
      contact: 'Contact',
      getStarted: 'Get Started',
    },
    // Hero
    hero: {
      tagline: 'Strategic Connections. Measurable Growth.',
      title: 'Grow your business with intelligent solutions',
      subtitle: 'VSP helps European businesses connect with customers, automate marketing, and drive sustainable growth.',
      cta: 'Request a Demo',
      ctaSecondary: 'Explore Services',
    },
    // Services
    services: {
      title: 'Our Services',
      subtitle: 'Everything you need to grow your business in the European market',
      crm: {
        title: 'CRM',
        desc: 'Manage customer relationships with a centralised platform designed for European businesses.',
        features: ['Contact management', 'Deal tracking', 'Pipeline automation', 'GDPR compliant'],
      },
      marketing: {
        title: 'Marketing',
        desc: 'Attract and engage your audience with powerful marketing automation tools.',
        features: ['Email campaigns', 'Landing pages', 'Lead generation', 'Analytics'],
      },
      sales: {
        title: 'Sales',
        desc: 'Close deals faster with tools that streamline your sales process.',
        features: ['Sales automation', 'Quote management', 'Meeting scheduler', 'Forecasting'],
      },
      service: {
        title: 'Service',
        desc: 'Deliver exceptional customer support that builds loyalty.',
        features: ['Ticketing system', 'Knowledge base', 'Live chat', 'Customer feedback'],
      },
      learnMore: 'Learn more',
    },
    // About
    about: {
      title: 'About VSP',
      subtitle: 'Your trusted partner for business growth in Europe',
      mission: {
        title: 'Our Mission',
        text: 'At VSP, we believe every European business deserves access to world-class tools for growth. We provide simplified, GDPR-compliant solutions tailored to the unique needs of the EU market.',
      },
      values: {
        title: 'Our Values',
        items: [
          { title: 'Simplicity', desc: 'Clear solutions without complexity' },
          { title: 'Trust', desc: 'GDPR compliant and data secure' },
          { title: 'Growth', desc: 'Measurable results for your business' },
          { title: 'Partnership', desc: 'We succeed when you succeed' },
        ],
      },
      euFocus: {
        title: 'European Focus',
        text: 'Unlike global platforms, VSP is built specifically for European businesses. We understand EU regulations, local markets, and the unique challenges you face.',
      },
    },
    // Contact
    contact: {
      title: 'Get in Touch',
      subtitle: 'Ready to grow your business? We\'re here to help.',
      form: {
        name: 'Name',
        email: 'Email',
        company: 'Company',
        message: 'Message',
        submit: 'Send Message',
        success: 'Message sent successfully!',
        error: 'Something went wrong. Please try again.',
      },
      demo: {
        title: 'Request a Demo',
        subtitle: 'See VSP in action',
        preferredDate: 'Preferred Date',
        preferredTime: 'Preferred Time',
        submit: 'Request Demo',
        success: 'Demo request submitted!',
      },
      info: {
        address: 'Address',
        phone: 'Phone',
        email: 'Email',
      },
    },
    // Footer
    footer: {
      tagline: 'Strategic Connections. Measurable Growth.',
      services: 'Services',
      company: 'Company',
      legal: 'Legal',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      cookies: 'Cookie Policy',
      rights: 'All rights reserved.',
    },
  },
  fr: {
    // Navigation
    nav: {
      home: 'Accueil',
      services: 'Services',
      about: 'À propos',
      contact: 'Contact',
      getStarted: 'Commencer',
    },
    // Hero
    hero: {
      tagline: 'Connexions stratégiques. Croissance mesurable.',
      title: 'Développez votre entreprise avec des solutions intelligentes',
      subtitle: 'VSP aide les entreprises européennes à se connecter avec leurs clients, automatiser le marketing et stimuler une croissance durable.',
      cta: 'Demander une démo',
      ctaSecondary: 'Découvrir nos services',
    },
    // Services
    services: {
      title: 'Nos Services',
      subtitle: 'Tout ce dont vous avez besoin pour développer votre entreprise sur le marché européen',
      crm: {
        title: 'CRM',
        desc: 'Gérez vos relations clients avec une plateforme centralisée conçue pour les entreprises européennes.',
        features: ['Gestion des contacts', 'Suivi des opportunités', 'Automatisation du pipeline', 'Conforme RGPD'],
      },
      marketing: {
        title: 'Marketing',
        desc: 'Attirez et engagez votre audience avec des outils d\'automatisation marketing puissants.',
        features: ['Campagnes email', 'Pages de destination', 'Génération de leads', 'Analytiques'],
      },
      sales: {
        title: 'Ventes',
        desc: 'Concluez des affaires plus rapidement avec des outils qui rationalisent votre processus de vente.',
        features: ['Automatisation des ventes', 'Gestion des devis', 'Planificateur de réunions', 'Prévisions'],
      },
      service: {
        title: 'Service Client',
        desc: 'Offrez un support client exceptionnel qui fidélise.',
        features: ['Système de tickets', 'Base de connaissances', 'Chat en direct', 'Feedback client'],
      },
      learnMore: 'En savoir plus',
    },
    // About
    about: {
      title: 'À propos de VSP',
      subtitle: 'Votre partenaire de confiance pour la croissance en Europe',
      mission: {
        title: 'Notre Mission',
        text: 'Chez VSP, nous croyons que chaque entreprise européenne mérite l\'accès à des outils de classe mondiale pour sa croissance. Nous fournissons des solutions simplifiées et conformes au RGPD, adaptées aux besoins uniques du marché européen.',
      },
      values: {
        title: 'Nos Valeurs',
        items: [
          { title: 'Simplicité', desc: 'Des solutions claires sans complexité' },
          { title: 'Confiance', desc: 'Conforme RGPD et données sécurisées' },
          { title: 'Croissance', desc: 'Des résultats mesurables pour votre entreprise' },
          { title: 'Partenariat', desc: 'Nous réussissons quand vous réussissez' },
        ],
      },
      euFocus: {
        title: 'Focus Européen',
        text: 'Contrairement aux plateformes mondiales, VSP est conçu spécifiquement pour les entreprises européennes. Nous comprenons les réglementations de l\'UE, les marchés locaux et les défis uniques auxquels vous êtes confrontés.',
      },
    },
    // Contact
    contact: {
      title: 'Contactez-nous',
      subtitle: 'Prêt à développer votre entreprise ? Nous sommes là pour vous aider.',
      form: {
        name: 'Nom',
        email: 'Email',
        company: 'Entreprise',
        message: 'Message',
        submit: 'Envoyer',
        success: 'Message envoyé avec succès !',
        error: 'Une erreur s\'est produite. Veuillez réessayer.',
      },
      demo: {
        title: 'Demander une démo',
        subtitle: 'Découvrez VSP en action',
        preferredDate: 'Date préférée',
        preferredTime: 'Heure préférée',
        submit: 'Demander une démo',
        success: 'Demande de démo envoyée !',
      },
      info: {
        address: 'Adresse',
        phone: 'Téléphone',
        email: 'Email',
      },
    },
    // Footer
    footer: {
      tagline: 'Connexions stratégiques. Croissance mesurable.',
      services: 'Services',
      company: 'Entreprise',
      legal: 'Mentions légales',
      privacy: 'Politique de confidentialité',
      terms: 'Conditions d\'utilisation',
      cookies: 'Politique des cookies',
      rights: 'Tous droits réservés.',
    },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('vsp-language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('vsp-language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'fr' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
